/* =============================================================
   AUTH — CONTRACT
   =============================================================
   The shape every auth backend must satisfy. The rest of the app
   only ever depends on these types, never on a specific provider,
   which is what makes swapping the local stand-in for Supabase a
   one-file change (`client.ts`).
   ============================================================= */

/** Account types — mirrors `User.role` in docs/data-model.md. */
export type UserRole = 'infrastructure_owner' | 'shipowner' | 'both'

export interface AuthUser {
  id: string
  email: string
  /** Person's name. */
  name: string
  /** Organisation the person acts for — shown in the top bar. */
  company: string
  /** That organisation's id: what owns their berths, vessels and bookings. */
  companyId: number
  role: UserRole
  avatarUrl?: string
}

export interface Session {
  user: AuthUser
  /** Epoch ms. The UI treats an expired session as signed out. */
  expiresAt: number
}

export interface SignInInput {
  email: string
  password: string
}

/** Thrown by every client method so the forms can render one message shape. */
export class AuthError extends Error {
  /** Stable, provider-independent code — useful for field-level mapping. */
  readonly code: string
  /** Form field this error belongs to, when it is about one field. */
  readonly field?: keyof SignInInput

  constructor(message: string, code = 'auth_error', field?: keyof SignInInput) {
    super(message)
    this.name = 'AuthError'
    this.code = code
    this.field = field
  }
}

export interface AuthClient {
  /** Current session, or null when signed out. Called once on boot. */
  getSession(): Promise<Session | null>
  signIn(input: SignInInput): Promise<Session>
  signOut(): Promise<void>
  /**
   * Subscribe to session changes (other tabs, token refresh, expiry).
   * Returns an unsubscribe function.
   */
  onAuthStateChange(callback: (session: Session | null) => void): () => void
}
